import { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Group,
  Text,
  Button,
  Loader,
  Center,
  Stack,
  Tabs,
  Badge,
  Modal,
  Box,
} from '@mantine/core';
import classes from './TaskTemplatesPage.module.css';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Plus } from 'react-feather';
import type { TemplateType, TaskTemplateWithDetails, PhaseWithSubGroups, TaskAnchorType } from '../../../types';
import { useTaskTemplates } from './hooks/useTaskTemplates';
import { PhaseAccordion, EditTemplateSidebar, CreateTemplateModal, SubGroupModal } from './components';
import { taskTemplatesApi } from '../../../services/api/taskTemplates';
import { taskPhasesApi } from '../../../services/api/taskPhases';
import { taskSubGroupsApi } from '../../../services/api/taskSubGroups';
import type { SubGroupWithTemplates } from '../../../types';

// Type for template counts by type
type TemplateTypeCounts = Record<TemplateType, number>;

// Template type tab config
const TEMPLATE_TYPE_TABS: { value: TemplateType; label: string; color: string }[] = [
  { value: 'base', label: 'Base Template', color: 'blue' },
  { value: 'group_coaching', label: 'Group Coaching', color: 'grape' },
  { value: 'one_on_one', label: '1:1 Coaching', color: 'teal' },
  { value: 'workshop', label: 'Workshop', color: 'orange' },
];

export function TaskTemplatesPage() {
  // Active template type tab
  const [activeTemplateType, setActiveTemplateType] = useState<TemplateType>('base');

  // Template counts for all types
  const [templateCounts, setTemplateCounts] = useState<TemplateTypeCounts>({
    base: 0,
    group_coaching: 0,
    one_on_one: 0,
    workshop: 0,
  });

  // Modal states
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editSidebarOpened, { open: openEditSidebar, close: closeEditSidebar }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [subGroupModalOpened, { open: openSubGroupModal, close: closeSubGroupModal }] = useDisclosure(false);

  // Selected items for modals
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplateWithDetails | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplateWithDetails | null>(null);
  const [createDefaults, setCreateDefaults] = useState<{ phaseId?: string; subGroupId?: string | null }>({});

  // Sub-group modal state
  const [subGroupModalMode, setSubGroupModalMode] = useState<'add' | 'edit'>('add');
  const [subGroupModalPhaseId, setSubGroupModalPhaseId] = useState<string>('');
  const [subGroupModalPhaseName, setSubGroupModalPhaseName] = useState<string>('');
  const [subGroupModalSubGroupCount, setSubGroupModalSubGroupCount] = useState<number>(0);
  const [selectedSubGroup, setSelectedSubGroup] = useState<SubGroupWithTemplates | null>(null);

  // Fetch data using hook
  const {
    phases,
    jobTitles,
    loading,
    error,
    refresh,
    archiveTemplate,
    restoreTemplate,
    duplicateTemplate,
    deleteTemplate,
  } = useTaskTemplates({
    templateType: activeTemplateType,
  });

  // Fetch template counts for all types
  const fetchTemplateCounts = useCallback(async () => {
    try {
      const counts = await taskTemplatesApi.getCountsByType();
      setTemplateCounts(counts);
    } catch (error) {
      console.error('Failed to fetch template counts:', error);
    }
  }, []);

  // Fetch counts on mount and when refresh is called
  useEffect(() => {
    fetchTemplateCounts();
  }, [fetchTemplateCounts]);

  // Combined refresh that updates both templates and counts
  const refreshAll = useCallback(async () => {
    await refresh();
    await fetchTemplateCounts();
  }, [refresh, fetchTemplateCounts]);

  // Get parent templates (non-subtasks) for create modal
  const parentTemplates = phases.flatMap(p =>
    p.sub_groups.flatMap(sg =>
      sg.templates.filter(t => !t.parent_template_id)
    )
  );

  // Handlers
  const handleAddTemplate = useCallback((subGroupId: string | null, phaseId?: string) => {
    setCreateDefaults({ phaseId, subGroupId: subGroupId });
    openCreateModal();
  }, [openCreateModal]);

  const handleEditTemplate = useCallback((template: TaskTemplateWithDetails) => {
    setSelectedTemplate(template);
    openEditSidebar();
  }, [openEditSidebar]);

  const handleDuplicateTemplate = useCallback(async (template: TaskTemplateWithDetails) => {
    await duplicateTemplate(template.id);
    await fetchTemplateCounts();
  }, [duplicateTemplate, fetchTemplateCounts]);

  const handleArchiveTemplate = useCallback(async (template: TaskTemplateWithDetails) => {
    await archiveTemplate(template.id);
    await fetchTemplateCounts();
  }, [archiveTemplate, fetchTemplateCounts]);

  const handleRestoreTemplate = useCallback(async (template: TaskTemplateWithDetails) => {
    await restoreTemplate(template.id);
    await fetchTemplateCounts();
  }, [restoreTemplate, fetchTemplateCounts]);

  const handleDeleteTemplate = useCallback((template: TaskTemplateWithDetails) => {
    setTemplateToDelete(template);
    openDeleteModal();
  }, [openDeleteModal]);

  const confirmDelete = useCallback(async () => {
    if (templateToDelete) {
      const success = await deleteTemplate(templateToDelete.id);
      if (success) {
        closeDeleteModal();
        setTemplateToDelete(null);
        await fetchTemplateCounts();
      }
    }
  }, [templateToDelete, deleteTemplate, closeDeleteModal, fetchTemplateCounts]);

  // Phase handlers
  const handleEditPhase = useCallback(async (phase: PhaseWithSubGroups) => {
    try {
      await taskPhasesApi.update(phase.id, { name: phase.name });
      notifications.show({
        title: 'Success',
        message: 'Phase updated successfully',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to update phase:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update phase',
        color: 'red',
      });
    }
  }, [refreshAll]);

  // Opens the edit sub-group modal
  const handleEditSubGroup = useCallback((subGroup: SubGroupWithTemplates) => {
    setSubGroupModalMode('edit');
    setSubGroupModalPhaseId(subGroup.phase_id);
    setSubGroupModalPhaseName(subGroup.phase_name || '');
    setSelectedSubGroup(subGroup);
    openSubGroupModal();
  }, [openSubGroupModal]);

  // Opens the add sub-group modal
  const handleOpenAddSubGroupModal = useCallback((phaseId: string, phaseName: string, subGroupCount: number) => {
    setSubGroupModalMode('add');
    setSubGroupModalPhaseId(phaseId);
    setSubGroupModalPhaseName(phaseName);
    setSubGroupModalSubGroupCount(subGroupCount);
    setSelectedSubGroup(null);
    openSubGroupModal();
  }, [openSubGroupModal]);

  const handleArchiveSubGroup = useCallback(async (subGroup: SubGroupWithTemplates) => {
    try {
      await taskSubGroupsApi.archive(subGroup.id);
      notifications.show({
        title: 'Success',
        message: 'Sub-group archived',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to archive sub-group:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to archive sub-group',
        color: 'red',
      });
    }
  }, [refreshAll]);

  const handleRestoreSubGroup = useCallback(async (subGroup: SubGroupWithTemplates) => {
    try {
      await taskSubGroupsApi.restore(subGroup.id);
      notifications.show({
        title: 'Success',
        message: 'Sub-group restored',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to restore sub-group:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to restore sub-group',
        color: 'red',
      });
    }
  }, [refreshAll]);

  const handleArchivePhase = useCallback(async (phase: PhaseWithSubGroups) => {
    try {
      await taskPhasesApi.archive(phase.id);
      notifications.show({
        title: 'Success',
        message: 'Phase archived',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to archive phase:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to archive phase',
        color: 'red',
      });
    }
  }, [refreshAll]);

  const handleRestorePhase = useCallback(async (phase: PhaseWithSubGroups) => {
    try {
      await taskPhasesApi.restore(phase.id);
      notifications.show({
        title: 'Success',
        message: 'Phase restored',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to restore phase:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to restore phase',
        color: 'red',
      });
    }
  }, [refreshAll]);

  const handleAddPhase = useCallback(async (name: string, position: 'start' | 'end' | string) => {
    try {
      // Determine sort_order based on position
      let sortOrder: number;
      if (position === 'start') {
        sortOrder = 0;
      } else if (position === 'end') {
        sortOrder = phases.length > 0 ? Math.max(...phases.map(p => p.sort_order)) + 1 : 0;
      } else if (position.startsWith('after:')) {
        const afterId = position.replace('after:', '');
        const afterPhase = phases.find(p => p.id === afterId);
        sortOrder = afterPhase ? afterPhase.sort_order + 1 : phases.length;
      } else {
        sortOrder = phases.length;
      }

      await taskPhasesApi.create({ name, sort_order: sortOrder });
      notifications.show({
        title: 'Success',
        message: 'Phase created',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to create phase:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create phase',
        color: 'red',
      });
    }
  }, [phases, refreshAll]);

  const handleReorderPhase = useCallback(async (phaseId: string, direction: 'up' | 'down') => {
    // Find the current phase and its neighbor
    const currentIndex = phases.findIndex(p => p.id === phaseId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= phases.length) return;

    // Swap sort_order values
    const currentPhase = phases[currentIndex];
    const targetPhase = phases[targetIndex];

    try {
      await taskPhasesApi.reorder([
        { id: currentPhase.id, sort_order: targetPhase.sort_order },
        { id: targetPhase.id, sort_order: currentPhase.sort_order },
      ]);
      await refreshAll();
    } catch (error) {
      console.error('Failed to reorder phases:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to reorder phases',
        color: 'red',
      });
    }
  }, [phases, refreshAll]);

  const handleCreateTaskInline = useCallback(async (data: {
    phaseId: string;
    subGroupId: string | null;
    taskTitle: string;
    jobTitleId: string | null;
    anchorType: TaskAnchorType;
    daysOffset: number;
    isSessionSpecific: boolean;
  }) => {
    try {
      await taskTemplatesApi.create({
        template_type: activeTemplateType,
        phase_id: data.phaseId,
        sub_group_id: data.subGroupId,
        task_title: data.taskTitle,
        job_title_id: data.jobTitleId,
        anchor_type: data.anchorType,
        days_offset: data.daysOffset,
        is_session_specific: data.isSessionSpecific,
        default_priority: 'medium',
        is_required: true,
      });
      notifications.show({
        title: 'Success',
        message: 'Task created successfully',
        color: 'green',
      });
      await refreshAll();
    } catch (error) {
      console.error('Failed to create task:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to create task',
        color: 'red',
      });
    }
  }, [activeTemplateType, refreshAll]);

  return (
    <>
      {/* Full-width header with shadow */}
      <Box className={classes.fullWidthHeader}>
        <Container size="xl" className={classes.headerContent}>
          <Group justify="space-between">
            <div>
              <Title order={2} className={classes.pageTitle}>Task Templates</Title>
              <Text className={classes.subtitle}>
                When a cohort is created, tasks are generated from Base + applicable modality templates
              </Text>
            </div>
            <Button
              leftSection={<Plus size={16} />}
              onClick={() => {
                setCreateDefaults({});
                openCreateModal();
              }}
            >
              Add Task
            </Button>
          </Group>

          {/* Template Type Tabs - inside header */}
          <Tabs
            value={activeTemplateType}
            onChange={v => v && setActiveTemplateType(v as TemplateType)}
            className={classes.tabsRow}
          >
            <Tabs.List>
              {TEMPLATE_TYPE_TABS.map(tab => (
                <Tabs.Tab key={tab.value} value={tab.value}>
                  <Group gap="xs">
                    <Text size="sm">{tab.label}</Text>
                    <Badge
                      size="xs"
                      variant="filled"
                      styles={{
                        root: {
                          backgroundColor: '#E8E6E3',
                          color: '#666666',
                        },
                      }}
                    >
                      {templateCounts[tab.value]}
                    </Badge>
                  </Group>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        </Container>
      </Box>

      {/* Main content area */}
      <Container size="xl" py="xl">
        <Tabs
          value={activeTemplateType}
          onChange={v => v && setActiveTemplateType(v as TemplateType)}
        >
          {/* Templates Panel */}
          <Tabs.Panel value={activeTemplateType}>
          {/* Content */}
          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : error ? (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <Text c="red">{error}</Text>
                <Button variant="light" onClick={refreshAll}>
                  Try Again
                </Button>
              </Stack>
            </Center>
          ) : phases.length === 0 ? (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <Text c="dimmed">No templates found for {activeTemplateType}</Text>
                <Button
                  variant="light"
                  onClick={() => {
                    setCreateDefaults({});
                    openCreateModal();
                  }}
                >
                  Create your first template
                </Button>
              </Stack>
            </Center>
          ) : (
            <PhaseAccordion
              phases={phases}
              jobTitles={jobTitles}
              onAddPhase={handleAddPhase}
              onEditPhase={handleEditPhase}
              onArchivePhase={handleArchivePhase}
              onRestorePhase={handleRestorePhase}
              onReorderPhase={handleReorderPhase}
              onOpenAddSubGroupModal={handleOpenAddSubGroupModal}
              onEditSubGroup={handleEditSubGroup}
              onArchiveSubGroup={handleArchiveSubGroup}
              onRestoreSubGroup={handleRestoreSubGroup}
              onAddTemplate={handleAddTemplate}
              onCreateTaskInline={handleCreateTaskInline}
              onEditTemplate={handleEditTemplate}
              onDuplicateTemplate={handleDuplicateTemplate}
              onArchiveTemplate={handleArchiveTemplate}
              onRestoreTemplate={handleRestoreTemplate}
              onDeleteTemplate={handleDeleteTemplate}
            />
          )}
          </Tabs.Panel>
        </Tabs>
      </Container>

      {/* Create Template Modal */}
      <CreateTemplateModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        onSuccess={refreshAll}
        templateType={activeTemplateType}
        phases={phases}
        jobTitles={jobTitles}
        parentTemplates={parentTemplates}
        defaultPhaseId={createDefaults.phaseId}
        defaultSubGroupId={createDefaults.subGroupId}
      />

      {/* Edit Template Sidebar */}
      <EditTemplateSidebar
        template={selectedTemplate}
        jobTitles={jobTitles}
        opened={editSidebarOpened}
        onClose={() => {
          closeEditSidebar();
          setSelectedTemplate(null);
        }}
        onSave={refreshAll}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setTemplateToDelete(null);
        }}
        title="Delete Template"
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete "{templateToDelete?.task_title}"?
          </Text>
          <Text size="xs" c="dimmed">
            This action cannot be undone. Existing tasks created from this template will not be affected.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                closeDeleteModal();
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Sub-Group Modal (Add/Edit) */}
      <SubGroupModal
        opened={subGroupModalOpened}
        onClose={() => {
          closeSubGroupModal();
          setSelectedSubGroup(null);
        }}
        onSuccess={refreshAll}
        mode={subGroupModalMode}
        phaseId={subGroupModalPhaseId}
        phaseName={subGroupModalPhaseName}
        subGroup={selectedSubGroup}
        existingSubGroupCount={subGroupModalSubGroupCount}
      />
    </>
  );
}

export default TaskTemplatesPage;
